/**
 * The wizard flows the assistant can recognize. Mirrors `AssetWizardFlow` in
 * allposit/resources/Ai/constants.py — the string values are the wire contract,
 * so the two enums have to be changed together.
 */
export enum AssetWizardFlow {
  LOAN = 'loan',
  /**
   * A fund commitment split across more than one investing entity — see
   * WizardInvestor. Was PRIVATE_FUND before the split across entities existed;
   * PRIVATE_FUND itself now names the single-owner case instead.
   */
  PRIVATE_FUND_SUBSCRIBE_AND_COMMIT = 'private_fund_subscribe_and_commit',
  /**
   * A single fund position, held in one account — no split, no per-investor
   * cash-flow log. The generic ownerAccount question every other flow asks.
   * See PRIVATE_FUND_SUBSCRIBE_AND_COMMIT for the fund split across more than
   * one investing entity.
   */
  PRIVATE_FUND = 'private_fund',
  /**
   * A private (non-fund) security — private company stock, notes, warrants —
   * or anything else none of the other flows cover. The catch-all: chosen
   * only when a more specific flow does not apply.
   */
  PRIVATE_SECURITY = 'private_security',
  REAL_ESTATE = 'real_estate',
  REAL_ESTATE_WITH_FINANCING = 'real_estate_with_financing',
  FINE_ART = 'fine_art',
  VEHICLE = 'vehicle',
}

/**
 * Fields the assistant is allowed to fill. Which of them a given flow carries is
 * the backend's `ASSET_WIZARD_FLOW_FIELDS`; the wizard on screen may show more
 * fields than these, but only these ever arrive prefilled.
 */
export enum AssetWizardField {
  ACCOUNT_NAME = 'accountName',
  /**
   * No flow lists this among `ASSET_WIZARD_FLOW_FIELDS` any more — the backend
   * rejects it for every flow. Kept only because removing the wire value is a
   * larger change than this one; nothing maps a field to it.
   */
  ACCOUNT_TYPE = 'accountType',
  ASSET_NAME = 'assetName',
  ASSET_TYPE = 'assetType',
  AMOUNT = 'amount',
  DATE = 'date',
  CURRENCY = 'currency',
  /**
   * loan's own: whether the amount was borrowed or lent. Read as one of
   * LoanDirection's own values ('Borrowed'/'Lent') — see Loan/schema.ts —
   * because that's the vocabulary the wizard's own field speaks, the same
   * choice CURRENCY makes for a ticker.
   */
  DIRECTION = 'direction',
  /**
   * Every flow's catch-all: a detail the sentence carried that none of the
   * other fields could hold. Lands in the wizard's own free-text description
   * field, exactly like any other field the sentence supported.
   */
  NOTES = 'notes',
  /**
   * real_estate_with_financing's own: the mortgage's borrowed amount,
   * distinct from AMOUNT (the property's purchase price).
   */
  PRINCIPAL = 'principal',
  /**
   * Also real_estate_with_financing's own: NOTES's sibling for this one
   * field — everything about the mortgage the sentence gives that is
   * neither ACCOUNT_NAME (the lender) nor PRINCIPAL (the amount), e.g. a
   * rate, a term, "adjustable", "refinanced from Wells Fargo".
   */
  ACCOUNT_DESCRIPTION = 'accountDescription',
}

/**
 * The account the records should hang off, as the read resolved it.
 *
 * Two shapes, one at a time. `id` is an account the repository already has, and
 * the wizard opens its ownership step on "use an existing account" pointing at
 * it. `name` + `type` is one to create, and the step opens on the other half
 * already filled in. Neither is a field the sentence stated — it is a match
 * against the repository — so it prefills rather than arriving read-only.
 *
 * `reason` is required in both shapes: an account chosen for the user is only
 * worth having if they can see why and overrule it.
 *
 * The backend guarantees the shape (see AssetWizardOwnerAccount). It cannot
 * guarantee the id, because the account list belongs to this side — that is
 * `readOwnerAccount`'s job, exactly as `readSuggestion` checks the account
 * suggestion's id against the candidates it offered.
 */
export type AssetWizardAccount = {
  id?: string
  name?: string
  type?: string
  reason: string
}

/**
 * A value the sentence supports without stating — a loan named for the lender
 * it mentions. The wizard writes it in and marks it the same as a stated
 * field: a second origin for "derived" drew the same sparkle and the same
 * summary treatment, so it is not a separate kind of value on screen.
 */
export type AssetWizardValueSuggestion = {
  value: string
  reason: string
}

/**
 * One investing entity's account and, where the sentence actually states a
 * split, its share — as the read resolved it. Carried by two flows:
 * private_fund_subscribe_and_commit's own answer to the question
 * `AssetWizardAccount` answers for every other flow, and private_security's
 * own version of the same thing — one entry per entity, in the same two
 * account shapes, plus (optionally, for private_security) that entity's
 * slice of the cost.
 *
 * `share` is an absolute amount, `percent` is a share of the whole amount —
 * never both. Resolving `percent` against the actual amount is
 * `seedInvestors`'s job, once the amount the sentence gave is known, the same
 * division `readOwnerAccount` draws against the id the backend cannot check.
 *
 * The backend guarantees the shape (see AssetWizardInvestor) and enforces
 * `share`/`percent` differently per flow: always present for
 * private_fund_subscribe_and_commit (that flow only exists because a split
 * was stated), optional for private_security (never chosen *for* a split).
 * See `AssetWizardEntity` for private_fund's own sibling, which never
 * carries either.
 */
export type AssetWizardInvestor = {
  id?: string
  name?: string
  type?: string
  share?: number
  percent?: number
  reason: string
}

/**
 * One entity independently holding the full private_fund position, as the
 * read resolved it. private_fund's own answer to the question
 * `AssetWizardAccount` answers for every other flow, and `AssetWizardInvestor`
 * answers for the two flows that can carry a share — one entry per entity,
 * in the same two account shapes, but no share or percent: nothing here
 * divides a commitment, so there is nothing to size a row by.
 *
 * The backend guarantees the shape (see AssetWizardOwnerAccount, reused for
 * this list on the backend too). Only private_fund ever carries this — see
 * `entitiesStepOf`. private_security used to as well, before it moved to
 * `AssetWizardInvestor`'s own `investors` so a row could carry a split.
 */
export type AssetWizardEntity = {
  id?: string
  name?: string
  type?: string
  reason: string
}

/**
 * One capital call, fee or distribution the sentence supported, against one
 * of this same answer's own `investors` (private_fund_subscribe_and_commit)
 * or `entities` (private_fund) — alongside that list itself.
 *
 * `investorIndex` addresses that list by position, not by an id: the
 * assistant is naming one of the entities it just described in the same
 * answer, and that list has no id of its own to point at yet — the account
 * it resolves to (existing or new) is what `seedInvestors`/`seedEntities`
 * decides, after this has already read the position. `seedCashFlows` is what
 * carries the position over onto the seeded `WizardInvestor[]` rows, which
 * keep the same order regardless of which of the two seeded them.
 *
 * The backend guarantees the shape (see AssetWizardCashFlow). Only the two
 * private_fund flows ever carry this — see `cashFlowsStepOf`.
 */
export type AssetWizardCashFlow = {
  investorIndex?: number
  /** ISO, as the step's own date control reads and writes it. */
  date?: string
  /** One of private_fund's own CONTRIBUTION_TYPES or DISTRIBUTION_TYPES. */
  type?: string
  amount?: number
  reason: string
}

/**
 * A dated value the sentence stated for the asset itself — an appraisal, a
 * current market estimate, a NAV — distinct from `fields.amount`, which is a
 * fact about the purchase or commitment, not about what the asset is worth
 * now. Feeds the Valuation step every flow but loan carries — see
 * `valueStepOf` and `seedMarks`.
 *
 * No investor/entity index, unlike `AssetWizardCashFlow`: a value names no
 * one, so there is nothing else to resolve it against.
 *
 * The backend guarantees the shape (see AssetWizardValuation).
 */
export type AssetWizardValuation = {
  /** ISO, as the step's own date control reads and writes it. */
  date?: string
  close?: number
  reason: string
}

/**
 * The assistant recognized a flow. Only fields it could support are present.
 *
 * Carries no tags. Those are a separate assistant's job (`ai_tag_suggestion`),
 * asked only when the user clicks "Ask AI" on the tags step — this read never
 * guesses them, so the wizard opens tagged with the flow's own defaults alone.
 */
export type AssetWizardAnswer = {
  flow: AssetWizardFlow
  /** Field name → value. A field the sentence did not state is absent, never blank. */
  fields: Partial<Record<AssetWizardField, string | number>>
  /** The flow's fields the sentence did not support, in the flow's own order. */
  missingFields: AssetWizardField[]
  /**
   * The existing account `fields.accountName` names, when it names one.
   *
   * Only on the flows that collect a held-in account — a fine art piece's
   * account, a mortgage's lender. It is what lets the create link to that
   * account instead of making a second one with the same name.
   */
  accountId?: string
  /**
   * Which account the records hang off. Only on the four flows whose wizard
   * asks — see ASSET_WIZARD_OWNER_ACCOUNT_FLOWS.
   */
  ownerAccount?: AssetWizardAccount
  /**
   * The entities that hold this position, each optionally carrying its own
   * share — private_fund_subscribe_and_commit's own commitment split (always
   * sized) and private_security's own investing entities (sized only where
   * the sentence actually states a split). See ASSET_WIZARD_OWNER_ACCOUNT_FLOWS's
   * own note on why neither flow is in that set.
   */
  investors?: AssetWizardInvestor[]
  /**
   * The entities that independently hold a private_fund position, each at
   * the full amount. Only on that flow — the two flows above share
   * `investors` instead.
   */
  entities?: AssetWizardEntity[]
  /**
   * Capital calls, fees and distributions against those same entities, when
   * the sentence supported any — either private_fund flow's own, alongside
   * `entities` or `investors`.
   */
  cashFlows?: AssetWizardCashFlow[]
  /**
   * A dated value for the asset itself, when the sentence supported one —
   * every flow but loan's own, distinct from `fields.amount`.
   */
  valuation?: AssetWizardValuation[]
  /** A name for the asset, when the sentence supports one without stating it. */
  assetNameSuggestion?: AssetWizardValueSuggestion
  /** One sentence on what the assistant read and what the wizard will create. */
  description: string
}

/**
 * The sentence matched no flow, or the model needed details it is not allowed to
 * invent. Carries no field values.
 */
export type AssetWizardRejection = {
  message: string
  questions?: string[]
  examples?: string[]
}

export type AssetWizardResponse = AssetWizardAnswer | AssetWizardRejection

export const isAssetWizardAnswer = (
  response: AssetWizardResponse,
): response is AssetWizardAnswer => 'flow' in response
